import { Box, Chip, Link, Stack, Typography } from '@mui/material';
import { FiExternalLink } from 'react-icons/fi';
import { MdCheckCircle } from 'react-icons/md';
import Input from 'components/Common/Input';
import Checkbox from 'components/Common/Checkbox';
import { BORDER_COLOR, PRIVACY_POLICY_TEMPLATE_URL, shrinkLabel, StepFormProps } from './campaignFormShared';

type Props = StepFormProps;

function PreparedChip({ ready }: { ready: boolean }) {
  if (!ready) return null;
  return (
    <Chip
      label='Prepared'
      size='small'
      sx={{
        bgcolor: '#DCFCE7',
        color: '#15803D',
        fontWeight: 500,
        height: 24,
        borderRadius: '999px'
      }}
    />
  );
}

export default function CompliancesStep({ control, watch }: Props) {
  const privacyReady = Boolean(watch('privacy_policy_link')?.trim()) && Boolean(watch('privacy_verified'));
  const termsReady = Boolean(watch('terms_and_conditions_link')?.trim()) && Boolean(watch('terms_verified'));

  return (
    <Stack spacing={2}>
      <Box border={`1px solid ${BORDER_COLOR}`} borderRadius='8px' p={2.5}>
        <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2}>
          <Typography fontSize={16} fontWeight={600}>
            Privacy Policy
          </Typography>
          <PreparedChip ready={privacyReady} />
        </Stack>
        <Typography fontSize={14} fontWeight={600} mb={0.5}>
          Your Privacy Policy must communicate the following disclosures to your patients:
        </Typography>
        <Typography fontSize={13} color='text.secondary' mb={1.5}>
          Privacy Policy should specify what details you collect and how customer data is processed.
        </Typography>
        <Link
          href={PRIVACY_POLICY_TEMPLATE_URL}
          target='_blank'
          rel='noreferrer'
          underline='none'
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 13, mb: 2, fontWeight: 500 }}
        >
          <FiExternalLink size={14} />
          Need Help? See Our Privacy Policy Template
        </Link>
        <Input
          name='privacy_policy_link'
          control={control}
          label='Privacy Policy URL'
          placeholder='https://www.example.com/privacy-policy'
          fullWidth
          required
          InputLabelProps={shrinkLabel}
          rules={{ required: 'Privacy Policy URL is required' }}
        />
        <Box mt={1}>
          <Checkbox
            name='privacy_verified'
            control={control}
            label='I verify my Privacy Policy contains the disclosures above.'
          />
        </Box>
        {privacyReady && (
          <Stack direction='row' spacing={0.75} alignItems='center' color='#16A34A' mt={0.5}>
            <MdCheckCircle size={16} />
            <Typography fontSize={13} color='#16A34A'>
              Privacy Policy,Prepared
            </Typography>
          </Stack>
        )}
      </Box>

      <Box border={`1px solid ${BORDER_COLOR}`} borderRadius='8px' p={2.5}>
        <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2}>
          <Typography fontSize={16} fontWeight={600}>
            Terms & Conditions
          </Typography>
          <PreparedChip ready={termsReady} />
        </Stack>
        <Typography fontSize={14} fontWeight={600} mb={0.5}>
          Your Terms & Conditions must communicate the following disclosures to your patients:
        </Typography>
        <Typography fontSize={13} color='text.secondary' mb={1}>
          Terms & Conditions should provide your patients with SMS disclosure by including information regarding the
          expectations for the following:
        </Typography>
        <Box component='ol' sx={{ pl: 2.5, mb: 1.5, mt: 0 }}>
          {[
            'Types of messages',
            'Texting cadence',
            'Message and data rate notices',
            'Privacy policy links',
            'Opt-out instructions'
          ].map((item) => (
            <Typography key={item} component='li' fontSize={13} color='text.primary' lineHeight={1.8}>
              {item}
            </Typography>
          ))}
        </Box>
        <Link
          href={PRIVACY_POLICY_TEMPLATE_URL}
          target='_blank'
          rel='noreferrer'
          underline='none'
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 13, mb: 2, fontWeight: 500 }}
        >
          <FiExternalLink size={14} />
          Need Help? See Our Privacy Policy Template
        </Link>
        <Input
          name='terms_and_conditions_link'
          control={control}
          label='Terms & Conditions URL'
          placeholder='https://example.com/terms'
          fullWidth
          required
          InputLabelProps={shrinkLabel}
          rules={{ required: 'Terms & Conditions URL is required' }}
        />
        <Box mt={1}>
          <Checkbox
            name='terms_verified'
            control={control}
            label='I verify my Terms & Conditions contain the disclosures above.'
          />
        </Box>
        {termsReady && (
          <Stack direction='row' spacing={0.75} alignItems='center' color='#16A34A' mt={0.5}>
            <MdCheckCircle size={16} />
            <Typography fontSize={13} color='#16A34A'>
              Terms & Conditions,Prepared
            </Typography>
          </Stack>
        )}
      </Box>
    </Stack>
  );
}
